import axiosInstance from "@/lib/axios";
import { BaseService } from "./BaseService";
import { CurrencyCode } from "@/utils/formatCurrency";

export interface Guest {
  id: string;
  createdAt: string;
  updatedAt: string;
  createdBy: string;
  updatedBy: string;
  ordering: number;
  name: string;
  status: string;
  numberOfGuests: number;
  guestNumber: string;
  message: null;
  wishDate: null;
  isShowWishList: boolean;
  guestTag: GuestTag;
  attendStatus: null;
  eventTemplateAlternativeUserId: string;
  invitedDate: null;
}

export interface GuestTag {
  id: string;
  name: string;
}

export interface TotalByCurrency {
  currency: string;
  total: number;
}

export interface TotalByPaymentType {
  paymentType: string;
  totals: TotalByCurrency[];
}

export interface GuestContribution {
  id: string;
  createdAt: string;
  updatedAt: string;
  createdBy: string;
  updatedBy: string;
  ordering: number;
  name: string;
  status: string;
  numberOfGuests: number;
  guestNumber: string;
  message: null;
  note: string;
  alternativeUserCurrencyId: string;
  amount: number;
  wishDate: null;
  isShowWishList: boolean;
  guestTag: GuestTag;
  attendStatus: null;
  eventTemplateAlternativeUserId: string;
  invitedDate: null;
  paymentType: string;
  paymentAt: string;
  alternativeUserCurrency: {
    id: string;
    createdAt: string;
    updatedAt: string;
    createdBy: string;
    updatedBy: string;
    ordering: number;
    name: string;
    code: CurrencyCode;
  };
}

export interface GuestContributionResponse {
  totalRecords?: number;
  records: GuestContribution[];
  lastId: string;
  totalByCurrency: TotalByCurrency[];
  totalByPaymentType: TotalByPaymentType[];
}

class GuestContributionService extends BaseService<GuestContribution> {
  constructor() {
    super("/api/guest-contributions");
  }

  async getAllByUser(
    query: Record<string, any>,
  ): Promise<GuestContributionResponse> {
    return await axiosInstance
      .get(`${this.endpoint}/scroll`, {
        params: query,
      })
      .then((response) => response.data.data);
  }

  async getOneContribution(id: string): Promise<GuestContribution> {
    return await axiosInstance
      .get(`${this.endpoint}/${id}`)
      .then((res) => res.data.data);
  }

  async getGuestSuggestions(
    alternativeUserId: string,
    params: Record<string, any>,
  ): Promise<Guest[]> {
    const response = await axiosInstance.get(
      `/api/guests/suggestions/${alternativeUserId}`,
      {
        params,
      },
    );
    return response.data.data;
  }

  async updateContribution(
    id: string,
    data: Partial<GuestContribution>,
  ): Promise<GuestContribution> {
    const response = await axiosInstance.put(`${this.endpoint}/${id}`, data);
    return response.data;
  }

  async createContribution(
    data: Partial<GuestContribution>,
  ): Promise<GuestContribution> {
    const response = await axiosInstance.post(this.endpoint, data);
    return response.data;
  }

  async getGuests(
    alternativeUserId: string,
    limit: number = 10000,
  ): Promise<Guest[]> {
    const response = await axiosInstance.get(`/api/guests`, {
      params: {
        eventTemplateAlternativeUserId: alternativeUserId,
        limit,
      },
    });
    return response.data.data;
  }
  async getGuestByAccountNumber(
    guestNumber: string,
    eventTemplateAlternativeUserId: string,
  ) {
    const response = await axiosInstance.get(`/api/self/guests/by-number`, {
      params: {
        guestNumber,
        eventTemplateAlternativeUserId,
      },
    });
    return response.data.data;
  }

  async deleteContribution(id: string): Promise<void> {
    await axiosInstance.delete(`${this.endpoint}/${id}`);
  }
}

export const guestContributionService = new GuestContributionService();
